// Design tokens carbon-copied from the approved welcome-tour prototype
// (docs/tc-tour.html). The tour owns its palette and type system deliberately —
// it is a full-screen surface, not a page inside the app shell.

// Brand type: Instrument Sans for display, Inter for body — the same pair the
// rest of the app and the marketing site use. Mono stays IBM Plex Mono.
export const FONT = {
  display: "'Instrument Sans', 'Inter', system-ui, sans-serif",
  body: "'Inter', system-ui, -apple-system, sans-serif",
  mono: "'IBM Plex Mono', monospace"
} as const;

// Two variants per accent, because a colour that reads well against a near-black
// panel glares against a near-white one. Bright values are dark mode; deep values
// are what light mode paints on borders, chips and buttons.
//
// The ink flips with them. On the bright fills, near-black is the readable pair;
// on the deep fills it is white — dark-on-mid-green clears the contrast maths but
// still reads muddy, so both deep colours are tuned to carry white above 5:1.
// Height of the fixed tour app bar. Shared, because the sticky task banner has
// to offset by exactly this much or it scrolls underneath it.
export const TOUR_BAR_H = 60;
// Top padding of the tour shell's scrolling body. The pinned demo stage sticks
// at exactly this offset, so it never jumps when the copy starts to scroll.
export const TOUR_CONTENT_PAD_TOP = 34;

// Brand green #007064 is the deep value light mode paints; dark mode uses the
// same hue lifted to stay legible on a near-black panel.
export const ACCENT = '#19C2AB';
export const ACCENT_DEEP = '#007064';
export const ACCENT_INK = '#08110D';
export const SOFT_ACCENT = 'rgba(25,194,171,.12)';
export const SOFT_ACCENT_DEEP = 'rgba(0,112,100,.10)';

// Label colour on a deep fill.
export const INK_ON_DEEP = '#FFFFFF';

export interface TourPalette {
  /** Which scheme this palette was built for — logo assets switch on it. */
  dark: boolean;
  btn: string;
  btnInk: string;
  btnHover: string;
  accent: string;
  accentInk: string;
  /** Darker tail for the primary-CTA gradient; ink contrast holds across both stops. */
  accentGradEnd: string;
  softAccent: string;
  accentMuted: string;
  aText: string;
  bg: string;
  fg: string;
  panel: string;
  card: string;
  well: string;
  line: string;
  line2: string;
  tipBg: string;
  sell: string;
  muted: number;
  muted2: number;
}

export const tourPalette = (dark: boolean): TourPalette => ({
  dark,
  accent: dark ? ACCENT : ACCENT_DEEP,
  accentInk: dark ? ACCENT_INK : INK_ON_DEEP,
  softAccent: dark ? SOFT_ACCENT : SOFT_ACCENT_DEEP,
  accentMuted: dark ? 'rgba(25,194,171,.4)' : 'rgba(0,112,100,.45)',
  // The deep fill doubles as the light-mode text colour. Measured on the tinted
  // card it sits on (not just the page, which was the earlier mistake): 5.41:1,
  // and white ink on it clears 6.00:1.
  aText: dark ? ACCENT : ACCENT_DEEP,
  // Near-black ink on the dark pair stays >=4.5:1 at the darker stop; white ink
  // on the light pair likewise (deep green family).
  accentGradEnd: dark ? '#12A38F' : '#00584E',
  // Light page sits a real step below the white panels (was #F6F7F5 — only a
  // 3% luminance gap, so cards dissolved into the page).
  bg: dark ? '#070B12' : '#EEF2F0',
  fg: dark ? '#E9EFEB' : '#0F1310',
  panel: dark ? '#0B111A' : '#FFFFFF',
  card: dark ? 'rgba(255,255,255,.04)' : 'rgba(15,19,16,.028)',
  well: dark ? 'rgba(255,255,255,.06)' : 'rgba(15,19,16,.05)',
  line: dark ? 'rgba(255,255,255,.11)' : 'rgba(15,19,16,.15)',
  line2: dark ? 'rgba(255,255,255,.2)' : 'rgba(15,19,16,.26)',
  tipBg: dark ? '#131A1D' : '#FFFFFF',
  // Commit buttons are INK, not brand accent: near-black on the light page,
  // warm white on the dark one. Accent keeps meaning selection and state.
  btn: dark ? '#E9EFEB' : '#131917',
  btnInk: dark ? '#0A0C0E' : '#FFFFFF',
  btnHover: dark ? '#FFFFFF' : '#2A322D',
  sell: dark ? '#FF6B6B' : '#C93636',
  // Secondary-text opacity. 0.5 reads fine on a near-black panel (4.73:1) but
  // only reaches 3.47:1 on the near-white one, so light mode needs its own step.
  muted: dark ? 0.5 : 0.74,
  muted2: dark ? 0.55 : 0.78
});

// The tour's keyframes, injected once by the tour shell.
export const TOUR_KEYFRAMES = {
  '@keyframes tcBlink': { to: { opacity: 0 } },
  '@keyframes tcPulse': { '0%,100%': { opacity: 0.35, transform: 'scale(1)' }, '50%': { opacity: 1, transform: 'scale(1.35)' } },
  '@keyframes tcRise': { from: { opacity: 0, transform: 'translateY(10px)' }, to: { opacity: 1, transform: 'none' } },
  '@keyframes tcRouteFlow': { to: { strokeDashoffset: -16 } },
  '@keyframes tcSvgPulse': { from: { transform: 'scale(.55)', opacity: 0.9 }, to: { transform: 'scale(1.7)', opacity: 0 } },
  '@keyframes tcFade': { from: { opacity: 0 }, to: { opacity: 1 } },
  '@keyframes tcRing': {
    '0%': { boxShadow: '0 0 0 0 rgba(25,194,171,.5)' },
    '70%': { boxShadow: '0 0 0 12px rgba(25,194,171,0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(25,194,171,0)' }
  },
  '@keyframes tcCloudUp': {
    from: { opacity: 0, transform: 'translateY(16px) scale(.98)', filter: 'blur(10px)' },
    to: { opacity: 1, transform: 'none', filter: 'none' }
  }
} as const;

/**
 * Secondary text on this surface is drawn by fading the near-black ink. That
 * reads well on the dark panel, but on a light page a 50%-transparent black is
 * a soft grey — the whole reason light mode looked washed rather than crisp.
 * Light gets a firmer floor, with the biggest lift on the faintest tiers so
 * hierarchy survives instead of flattening.
 */
export const dim = (p: TourPalette, v: number): number => {
  if (p.dark) return v;
  const lift = v < 0.6 ? 0.18 : v < 0.8 ? 0.12 : 0.06;
  return Math.min(1, Number((v + lift).toFixed(2)));
};
